import { Box, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { CardListRow } from '@/components/Card/CardListRow.tsx';
import { WidgetParameter } from '@/models/widget';
import { groupDesignParams } from '@/pages/widgetEditor/utils';
import { WidgetParameterControl } from '@/pages/widgetEditor/components/WidgetParameterControl';

interface WidgetDesignFieldsProps {
    names: string[];
    getParam: (name: string) => WidgetParameter | undefined;
    getValue: (name: string) => string;
    /** Shown read-only when the instance follows the shared design. */
    disabled?: boolean;
    onChange: (name: string, value: string) => void;
}

/**
 * Design knobs grouped into titled sections, at most two fields per row.
 *
 * Shared by the Design card and each widget's override section, so both look
 * the same. Which value a field shows is the caller's business: this only lays
 * the fields out.
 */
export const WidgetDesignFields: FunctionComponent<WidgetDesignFieldsProps> = ({
    names,
    getParam,
    getValue,
    disabled = false,
    onChange,
}) => {
    const sections = groupDesignParams(
        names.filter((name) => getParam(name) !== undefined)
    );

    return (
        <>
            {sections.map(({ title, rows }) => (
                <CardListRow key={title} withBorderBottom>
                    <Typography variant='subtitle2' sx={{ mb: 2 }}>
                        {title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {rows.map((row) => (
                            <Box
                                key={row.join('-')}
                                sx={{
                                    display: 'grid',
                                    // A lone field keeps half the width, so
                                    // columns line up across rows and sections.
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1fr 1fr',
                                    },
                                    gap: 3,
                                }}
                            >
                                {row.map((name) => (
                                    <WidgetParameterControl
                                        key={name}
                                        name={name}
                                        param={getParam(name)!}
                                        value={getValue(name)}
                                        disabled={disabled}
                                        onChange={(value) =>
                                            onChange(name, value)
                                        }
                                    />
                                ))}
                            </Box>
                        ))}
                    </Box>
                </CardListRow>
            ))}
        </>
    );
};
